import type { Assessment, Course, Lesson, Module, TrackGroup } from "@/types/course";
import { checkSequentialAccess, type UniversalAssessmentRequirement } from "@/lib/lesson-access";

export const COURSE_COMPLETION_THRESHOLD = 0.75;

export type LessonProgressStatus = {
  completed: boolean;
  percentWatched: number;
};

export type AssessmentPassMap = Map<string, Map<string, boolean>>;
export type TrackProgressMap = Map<string, string[]>;
export type UniversalRequirementMap = Map<string, UniversalAssessmentRequirement[]>;

export function findLessonInCourse(
  course: Course,
  lessonSlug: string
): { lesson: Lesson; module: Module; moduleIndex: number; lessonIndex: number } | null {
  for (let moduleIndex = 0; moduleIndex < course.modules.length; moduleIndex += 1) {
    const module = course.modules[moduleIndex];
    const lessonIndex = module.lessons.findIndex((lesson) => lesson.slug === lessonSlug);
    if (lessonIndex >= 0) {
      return {
        lesson: module.lessons[lessonIndex],
        module,
        moduleIndex,
        lessonIndex,
      };
    }
  }

  return null;
}

export function findModuleAssessment(
  course: Course,
  moduleId: string,
  assessmentIdOrSlug: string
): { module: Module; assessment: Assessment } | null {
  const module = course.modules.find((item) => item.id === moduleId);
  const assessment = module?.assessments?.find(
    (item) => item.id === assessmentIdOrSlug || item.slug === assessmentIdOrSlug
  );

  return module && assessment ? { module, assessment } : null;
}

export function buildLessonProgressMap(
  rows: Array<{ lesson_id: string; completed: boolean | null; percent_watched: number | null }>
): Map<string, LessonProgressStatus> {
  return new Map(
    rows.map((row) => {
      const percentWatched = Number(row.percent_watched ?? 0);
      return [
        row.lesson_id,
        {
          completed: Boolean(row.completed) || percentWatched >= COURSE_COMPLETION_THRESHOLD,
          percentWatched,
        },
      ];
    })
  );
}

export function buildAssessmentPassMap(
  rows: Array<{ module_id: string; assessment_id: string; passed: boolean | null }>
): AssessmentPassMap {
  const map: AssessmentPassMap = new Map();

  rows.forEach((row) => {
    if (!map.has(row.module_id)) {
      map.set(row.module_id, new Map());
    }
    map.get(row.module_id)?.set(row.assessment_id, Boolean(row.passed));
  });

  return map;
}

export function buildTrackProgressMap(
  rows: Array<{ track_group_id: string; completed_track_module_ids: string[] | null }>
): TrackProgressMap {
  const map: TrackProgressMap = new Map();

  rows.forEach((row) => {
    map.set(row.track_group_id, row.completed_track_module_ids ?? []);
  });

  return map;
}

export function getTrackGroupForModule(course: Course, module: Module): TrackGroup | undefined {
  if (!module.isTrack || !module.trackGroupId) {
    return undefined;
  }

  return course.trackGroups?.find((trackGroup) => trackGroup.id === module.trackGroupId);
}

export function validateTrackCompletionRequest(
  course: Course,
  trackGroupId: string,
  moduleId: string,
  progressMap: Map<string, LessonProgressStatus>
): { valid: true; trackGroup: TrackGroup; module: Module } | { valid: false; error: string } {
  const trackGroup = course.trackGroups?.find((item) => item.id === trackGroupId);
  if (!trackGroup) {
    return { valid: false, error: "track_group_not_found" };
  }

  const module = course.modules.find((item) => item.id === moduleId);
  if (!module || !module.isTrack || module.trackGroupId !== trackGroupId) {
    return { valid: false, error: "track_module_not_found" };
  }

  if (!trackGroup.trackModuleIds.includes(moduleId)) {
    return { valid: false, error: "track_module_not_in_group" };
  }

  const allLessonsCompleted = module.lessons.every((lesson) => {
    const progress = progressMap.get(lesson.slug);
    return Boolean(progress?.completed);
  });

  if (!allLessonsCompleted) {
    return { valid: false, error: "track_lessons_incomplete" };
  }

  return { valid: true, trackGroup, module };
}

export function chooseNextCourseLesson(
  course: Course,
  progressMap: Map<string, LessonProgressStatus>,
  assessmentMap?: AssessmentPassMap,
  universalAssessmentMap?: UniversalRequirementMap,
  trackProgressMap?: TrackProgressMap
): string | null {
  const visitedTrackGroups = new Set<string>();

  for (const module of course.modules) {
    if (module.isTrack && module.trackGroupId) {
      if (visitedTrackGroups.has(module.trackGroupId)) {
        continue;
      }
      visitedTrackGroups.add(module.trackGroupId);

      const trackGroup = course.trackGroups?.find((item) => item.id === module.trackGroupId);
      if (!trackGroup) {
        continue;
      }

      const completedTrackIds = trackProgressMap?.get(trackGroup.id) ?? [];
      const selectedTrackIds =
        completedTrackIds.length > 0
          ? completedTrackIds
          : trackGroup.trackModuleIds.filter((id) => {
              const trackModule = course.modules.find((item) => item.id === id);
              return trackModule?.lessons.some((lesson) => progressMap.has(lesson.slug));
            });

      const candidateTrackIds =
        selectedTrackIds.length > 0 ? selectedTrackIds : trackGroup.trackModuleIds.slice(0, 1);

      for (const trackModuleId of candidateTrackIds) {
        const trackModule = course.modules.find((item) => item.id === trackModuleId);
        if (!trackModule) {
          continue;
        }
        const nextLesson = firstAccessibleIncompleteLesson(
          course,
          trackModule.lessons,
          progressMap,
          assessmentMap,
          universalAssessmentMap,
          trackProgressMap
        );
        if (nextLesson) {
          return nextLesson;
        }
      }

      if (completedTrackIds.length < trackGroup.requiredTracksCount) {
        const nextTrackId = trackGroup.trackModuleIds.find((id) => !completedTrackIds.includes(id));
        const nextTrack = course.modules.find((item) => item.id === nextTrackId);
        return nextTrack?.lessons[0]?.slug ?? null;
      }

      continue;
    }

    const nextLesson = firstAccessibleIncompleteLesson(
      course,
      module.lessons,
      progressMap,
      assessmentMap,
      universalAssessmentMap,
      trackProgressMap
    );
    if (nextLesson) {
      return nextLesson;
    }
  }

  return course.modules.find((module) => module.lessons.length > 0)?.lessons[0]?.slug ?? null;
}

function firstAccessibleIncompleteLesson(
  course: Course,
  lessons: Lesson[],
  progressMap: Map<string, LessonProgressStatus>,
  assessmentMap?: AssessmentPassMap,
  universalAssessmentMap?: UniversalRequirementMap,
  trackProgressMap?: TrackProgressMap
) {
  for (const lesson of lessons) {
    const progress = progressMap.get(lesson.slug);
    if (progress?.completed) {
      continue;
    }

    const access = checkSequentialAccess(
      course,
      lesson.slug,
      progressMap,
      assessmentMap,
      universalAssessmentMap,
      trackProgressMap
    );

    if (access.hasAccess) {
      return lesson.slug;
    }
  }

  return null;
}

export function gradeModuleAssessment(
  assessment: Assessment,
  responses: Record<string, unknown>
): { score: number; passingScore: number; passed: boolean } {
  let autoEvaluated = 0;
  let autoCorrect = 0;

  assessment.questions.forEach((question) => {
    let isCorrect: boolean | null = null;

    switch (question.type) {
      case "multiple-choice": {
        autoEvaluated += 1;
        const response = responses[question.id];
        const selected = typeof response === "string" ? response : null;
        isCorrect = Boolean(question.options?.find((option) => option.id === selected)?.isCorrect);
        break;
      }
      case "multiple-select": {
        autoEvaluated += 1;
        const response = responses[question.id];
        const selected = Array.isArray(response) ? response.filter((value) => typeof value === "string") : [];
        const correct = (question.options ?? [])
          .filter((option) => option.isCorrect)
          .map((option) => option.id);
        const selectedSet = new Set(selected);
        const correctSet = new Set(correct);
        isCorrect = selectedSet.size === correctSet.size && selected.every((value) => correctSet.has(value));
        break;
      }
      case "true-false": {
        autoEvaluated += 1;
        const response = responses[question.id];
        isCorrect = typeof response === "string" && response === (question.correctResponse ?? "true");
        break;
      }
      default:
        isCorrect = null;
    }

    if (isCorrect) {
      autoCorrect += 1;
    }
  });

  const score = autoEvaluated > 0 ? (autoCorrect / autoEvaluated) * 100 : 100;
  const passingScore = assessment.passingScore ?? 70;

  return {
    score,
    passingScore,
    passed: score >= passingScore,
  };
}
